/**
 * Mapping Job Service (#97)
 *
 * Verbindet den Job-Monitor mit dem Batch-Mapping-Job-Endpunkt. Anders als
 * die uebrigen Mapping-Aufrufe in `catalogService.js` liegt dieser Endpunkt
 * laut Ticket direkt unter `/api/`, nicht unter `/api/olymp/` -- deshalb hier
 * eine eigene URL-Bildung statt Wiederverwendung von `catalogService`s
 * `OLYMP_API_BASE_URL`. Der eigentliche Request laeuft seit Runde 3 (#93,
 * Blocker B1) ueber `httpClient.apiRequest` -- vorher gab es hier einen
 * zweiten, redundanten fetch/Timeout/Fehler-Client neben dem in `./httpClient`
 * gebuendelten, mit einem eigenen, schwaecheren Fehlermodell (nur
 * "API error <status>", ohne Backend-Meldung). Oeffentliche API dieses Moduls
 * unveraendert -- JobMonitor.js importiert weiterhin von hier.
 */
import { API_BASE_URL } from '../config/api';
import { apiRequest } from './httpClient';

export const MAPPING_JOB_END_STATES = ['completed', 'failed', 'cancelled', 'stopped_budget'];

export function isMappingJobEndState(status) {
  return MAPPING_JOB_END_STATES.includes(status);
}

function buildJobUrl(jobId, action = '') {
  const base = `${API_BASE_URL}/mapping/jobs/${jobId}`.replace(/\/+$/, '');
  const normalizedAction = String(action).replace(/^\/+|\/+$/g, '');
  return normalizedAction ? `${base}/${normalizedAction}/` : `${base}/`;
}

function toFiniteNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// Intl.NumberFormat wirft RangeError fuer jeden Wert, der keinem wohlgeformten
// ISO-4217-Code entspricht (z.B. "$", "Euro", "EU") -- und zwar erst beim
// Rendern, unabhaengig davon, ob der Code eine echte Waehrung bezeichnet.
// Ohne diese Pruefung blankt eine kaputte Backend-Antwort die ganze App, weil
// hier keine ErrorBoundary sitzt.
const WELL_FORMED_CURRENCY_CODE = /^[A-Za-z]{3}$/;

function toCurrencyCode(value, fallback = 'EUR') {
  return typeof value === 'string' && WELL_FORMED_CURRENCY_CODE.test(value)
    ? value.toUpperCase()
    : fallback;
}

function normalizeJobError(error, index) {
  if (!error) {
    return null;
  }
  if (typeof error === 'string') {
    return { id: String(index), message: error, parameterId: null };
  }
  if (typeof error !== 'object') {
    return null;
  }
  return {
    id: error.id !== undefined && error.id !== null ? String(error.id) : String(index),
    message: error.message || '',
    parameterId: error.parameterId ?? null,
  };
}

/**
 * Normalisiert die Job-Antwort auf eine feste Form, unabhaengig davon, ob
 * das Backend `progress: {done, total}` oder flache `done`/`total`-Felder
 * liefert (im Contract nicht endgueltig festgelegt -- siehe `## Annahmen`).
 */
export function normalizeMappingJob(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const progress = data.progress && typeof data.progress === 'object' ? data.progress : {};
  const errors = (Array.isArray(data.errors) ? data.errors : [])
    .map(normalizeJobError)
    .filter(Boolean);

  return {
    id: data.id ?? null,
    status: data.status || 'unknown',
    done: toFiniteNumber(progress.done ?? data.done, 0),
    total: toFiniteNumber(progress.total ?? data.total, 0),
    cost: toFiniteNumber(data.cost, 0),
    currency: toCurrencyCode(data.currency),
    errorCount: Number.isFinite(data.errorCount) ? data.errorCount : errors.length,
    errors,
  };
}

export async function getMappingJob(jobId) {
  const data = await apiRequest(buildJobUrl(jobId));
  return normalizeMappingJob(data);
}

export async function cancelMappingJob(jobId) {
  const data = await apiRequest(buildJobUrl(jobId, 'cancel'), { method: 'POST', body: {} });
  return normalizeMappingJob(data);
}

export default {
  getMappingJob,
  cancelMappingJob,
  isMappingJobEndState,
  normalizeMappingJob,
  MAPPING_JOB_END_STATES,
};
